/* eslint-disable @typescript-eslint/no-explicit-any */
import { OperatorsEnum } from '@celito.clients/enums';
import { ObjectAttributeDefinition } from '@celito.clients/types';
import { isMajorVersion } from '@celito.clients/utils';
import {
  IFilterView,
  ISimplifedFilter,
} from 'libs/shared/src/lib/rules-component/types/rules-component.types';

interface OutboundRelationships {
  [key: string]: {
    column: string;
    result: unknown[];
  };
}

interface ConditionNode {
  all: unknown[];
  any: unknown[];
}

// Ensure a condition node has both 'all' and 'any' arrays.
// Mutates the passed object and returns it for convenience.
function ensureAnyAll(
  node: ConditionNode | undefined
): ConditionNode | undefined {
  if (!node) return node;
  const hasAllItems = Array.isArray(node.all) && node.all.length > 0;
  const hasAnyItems = Array.isArray(node.any) && node.any.length > 0;

  // Only add missing arrays if both sides are empty/missing.
  // This avoids creating an empty 'all' when 'any' already has content (and vice versa).
  if (node.all === undefined && !hasAnyItems) node.all = [];
  if (node.any === undefined && !hasAllItems) node.any = [];
  return node;
}

// Returns true if a condition node is effectively empty:
// - root has no items in both arrays, or
// - only contains groups that are themselves empty (no rules inside)
function isConditionsEmpty(node: ConditionNode | undefined): boolean {
  if (!node) return true;

  const all = Array.isArray(node.all) ? (node.all as unknown[]) : [];
  const any = Array.isArray(node.any) ? (node.any as unknown[]) : [];

  const isEmptyGroup = (g: unknown): boolean => {
    if (
      typeof g === 'object' &&
      g !== null &&
      Array.isArray((g as ConditionNode).all) &&
      Array.isArray((g as ConditionNode).any)
    ) {
      const group = g as ConditionNode;
      return group.all.length === 0 && group.any.length === 0;
    }
    return false;
  };

  const allEmpty = all.length === 0 || all.every(isEmptyGroup);
  const anyEmpty = any.length === 0 || any.every(isEmptyGroup);

  return allEmpty && anyEmpty;
}

export function getFiltersToApply(
  selectedFilterView?: IFilterView,
  recordName?: string,
  recordVersion?: string,
  referenceFilter?: ObjectAttributeDefinition,
  objectName?: string,
  viewObjectName?: string,
  outboundRelationships?: OutboundRelationships,
  filterByParentObjectName?: boolean,
  parentObjectName?: string
) {
  const filtersToApply: any = selectedFilterView?.filtersToBeApplied[0] ?? {};

  let newFilter: ISimplifedFilter | undefined;
  let parentObjectFilter: ISimplifedFilter | undefined;

  // Related object default filter is parent record name and version
  if (referenceFilter) {
    newFilter = {
      attribute: referenceFilter?.columnName,
      operator: OperatorsEnum.EQUALS,
      referenceFilters: [
        {
          conditions: {
            all: [
              {
                all: [
                  {
                    attribute: 'name',
                    operator: 'equals',
                    value: recordName,
                  },
                  ...(recordVersion && isMajorVersion(recordVersion)
                    ? [
                        {
                          attribute: 'version',
                          operator: 'equals',
                          value: recordVersion,
                        },
                      ]
                    : []),
                ],
              },
            ],
          },
        },
      ],
    };
  } else if (viewObjectName === 'extension_request__s' && recordName) {
    // Special handling for extension_request__s
    newFilter = {
      attribute: 'object_record_name__s',
      operator: OperatorsEnum.EQUALS,
      value: recordName,
    };
  } else if (viewObjectName && outboundRelationships?.[viewObjectName]) {
    newFilter = {
      attribute: outboundRelationships[viewObjectName].column,
      operator: OperatorsEnum.IN,
      value: outboundRelationships[viewObjectName].result,
    };
  }

  if (filterByParentObjectName && parentObjectName) {
    parentObjectFilter = {
      attribute: 'objectDefinitionName',
      operator: OperatorsEnum.EQUALS,
      value: parentObjectName,
    };
  }

  // If there is no new filter to add and existing conditions are effectively empty, return {}
  if (
    !newFilter &&
    !parentObjectFilter &&
    isConditionsEmpty(filtersToApply.conditions as unknown as ConditionNode)
  ) {
    return {};
  }

  // Ensure each first-level group has both keys
  if (Array.isArray(filtersToApply?.conditions?.all)) {
    filtersToApply.conditions.all = filtersToApply.conditions.all.map(
      (group: ConditionNode) => ensureAnyAll(group) as ConditionNode
    );
  }
  if (Array.isArray(filtersToApply?.conditions?.any)) {
    filtersToApply.conditions.any = filtersToApply.conditions.any.map(
      (group: ConditionNode) => ensureAnyAll(group) as ConditionNode
    );
  }

  if (newFilter) {
    createNewAppendFilter(filtersToApply, newFilter);
  }

  if (parentObjectFilter) {
    createNewAppendFilter(filtersToApply, parentObjectFilter);
  }

  return filtersToApply;
}

const createNewAppendFilter = (
  filtersToApply: {
    conditions?: {
      all?: unknown[];
      any?: unknown[];
    };
  },
  newFilter: ISimplifedFilter
) => {
  // Normalize the structure to always have both 'any' and 'all'.
  filtersToApply.conditions ??= {};

  // Ensure root structure
  ensureAnyAll(filtersToApply.conditions as ConditionNode);
  if (!Array.isArray(filtersToApply.conditions.all)) {
    filtersToApply.conditions.all = [];
  }
  // Append a new root-level group with the newFilter as an AND condition
  const newGroup: ConditionNode = {
    all: [newFilter as unknown],
  } as unknown as ConditionNode;

  filtersToApply.conditions.all.push(ensureAnyAll(newGroup)!);
};
